import styled from 'styled-components'
import React, {useEffect, useState} from 'react'
import TextField from '@material-ui/core/TextField'
import {handleAutocomplete, onChangeStringHandler} from 'lib/dom'
import {Autocomplete} from '@material-ui/lab'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {ajax, useObserve} from 'lib/rx'
import {debounceTime, filter, map, switchMap, tap} from 'rxjs/operators'
import {useAuthToken} from 'obvio/auth'
import Button from '@material-ui/core/Button'
import {useToggle} from 'lib/toggle'
import Dialog from 'lib/ui/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import CreateNewFieldForm from 'organization/Event/Services/Apps/HighLevel/Config/CreateNewFieldForm'
import {HighLevelField} from 'organization/Event/Services/Apps/HighLevel'

/**
 * Minimum amount of chars required to perform a search.
 */
const MIN_SEARCH_CHARS = 3

export default function FieldsAutocomplete(props: {
  value: HighLevelField | null
  onChange: (highLevelField: HighLevelField | null) => void
  inputLabel?: string
  inputVariant?: 'filled' | 'standard' | 'outlined' | undefined
  saveButtonText?: string
  disabled?: boolean
  errorText?: string
  types?: string[]
}) {
  const {value, onChange, types} = props
  const [name, setName] = useState('')
  const {value$, onReady} = useObserve(name)
  const [options, setOptions] = React.useState<HighLevelField[]>([])
  const [loading, setLoading] = React.useState(false)
  const {flag: showCreateForm, toggle: toggleShowCreateForm} = useToggle()

  const {
    event: {slug},
  } = useEvent()
  const token = useAuthToken()

  const handleOnCreate = (newField: HighLevelField) => {
    toggleShowCreateForm()
    onChange(newField)
  }

  useEffect(() => {
    if (!onReady) {
      return
    }

    value$
      .pipe(
        filter((value) => {
          return value.length >= MIN_SEARCH_CHARS
        }),
        tap(() => {
          setLoading(true)
        }),
        debounceTime(500),
        switchMap((name) => {
          const url = api(
            `/events/${slug}/integrations/high_level/fields?search=${name}`,
          )

          return ajax.get(url, {
            accessToken: token,
          })
        }),
        map((res) => res.response),
        tap(() => {
          setLoading(false)
        }),
      )
      .subscribe({
        next: (options) => {
          const filteredOptions = options.filter((option: HighLevelField) => {
            if (types) {
              return types.includes(option.field_data_type)
            }

            return true
          })

          setOptions(filteredOptions)
        },
      })

    onReady()
  }, [value$, token, slug, onReady, types])

  return (
    <Box>
      <Autocomplete
        disablePortal
        options={options}
        value={value}
        onChange={handleAutocomplete(onChange)}
        getOptionLabel={(option) => (option ? option.field_name : '')}
        loading={loading}
        closeIcon=""
        aria-label={'field id holder'}
        noOptionsText={'Type to search. Minimum length: 3 characters.'}
        getOptionSelected={(option, value) =>
          option.field_id === value.field_id
        }
        renderInput={(params) => {
          return (
            <>
              <TextField
                {...params}
                onChange={onChangeStringHandler(setName)}
                variant={props.inputVariant ? props.inputVariant : 'standard'}
                fullWidth
                helperText={props.errorText ? props.errorText : ''}
                error={Boolean(props.errorText)}
                label={props.inputLabel ? props.inputLabel : 'High level Field'}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'field id',
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Button
                      type="submit"
                      color="primary"
                      size="large"
                      aria-label="create field"
                      onClick={toggleShowCreateForm}
                    >
                      Create new field
                    </Button>
                  ),
                }}
              />
            </>
          )
        }}
      />
      <CreateNewFieldDialog
        visible={showCreateForm}
        onClose={toggleShowCreateForm}
        onCreate={handleOnCreate}
        types={types}
      />
    </Box>
  )
}

const CreateNewFieldDialog = (props: {
  visible: boolean
  onClose: () => void
  onCreate: (newTag: HighLevelField) => void
  types?: string[]
}) => {
  const {visible, onCreate, onClose, types} = props

  return (
    <Dialog open={visible} onClose={onClose}>
      <DialogTitle>Create new field</DialogTitle>
      <DialogContent>
        <CreateNewFieldForm onCreate={onCreate} types={types} />
      </DialogContent>
    </Dialog>
  )
}

const Box = styled.div`
  position: relative;
  .MuiAutocomplete-popper {
    margin-top: -20px !important;
  }
`
